import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';
import classNames from 'classnames/bind';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import qs from 'query-string';

// State
import { addToCart } from '../../state/modules/catalog/actions';
import { changeAvailableVariations } from '../../state/modules/constructor/actions';
import { removeItem, updateItem } from '../../state/modules/cart/actions';
import { showFreeSaucesPanel } from '../../state/modules/freeSauces/actions';

// Router
import { getRoute } from '../../entry/routes';

// Components
import Heading from '../Heading';
import Subheading from '../Subheading';
import Button from '../Button';
import Amount from '../Amount';
import PizzaSelector from '../PizzaSelector';
import SizeSelector from '../SizeSelector';
import Ingredients from './Ingredients';
import AlcoholWarning from './AlcoholWarning';
import Characteristics from './Characteristics';
import Flipper from './Flipper';
import MetaOption from './MetaOption';
import Crust from './Crust';
import ProductCardModal from './ProductCardModal';

// Icons
import GiftIcon from '../../icons/gift.svg';
import CheeseIcon from '../../icons/cheese.svg';

// Utils
import {
  isAlcohol,
  isHalal,
  isHit,
  isHot,
  isNew,
  isPizza,
  isVegeterian,
} from '../../utils/catalog';
import {
  getDoughTypes,
  getPersonCount,
  getRemovedIngredientsCount,
  getSizes,
  getStuffedCrusts,
  getVariation,
  stuffedCrustIsAvailable as getStuffedCrustIsAvailable,
  stuffedCrustsAvailable as getStuffedCrustsAvailable,
} from '../../state/modules/catalog/utils';
import { CRUST_EXTRA, CRUST_EXTRA_EN, CRUST_EXTRA_PL } from '../../utils/constants';

// Styles
import styles from './ProductCard.styl';

const cx = classNames.bind(styles);

export default function ProductCard(props) {
  const {
    className,
    stockRole,
    good,
    addToCartAction,
    removeIngredientAction,
    cancelRemovedIngredientsAction,
    // type может придти только из StockGoods - потому что там есть подарки
    type,
    // если мы открываем эту карту из ProductCardCompact
    compact,
    isFromProductList,
    category: categoryName,
    isModalOpenPhone,
    isPromo,
    isLoadingStockApply,
    isHiddenActions,
  } = props;

  const {
    id,
    name,
    alias,
    data,
    types,
    show_ingredients_button,
    variations,
    isAdding,
    good_type,
    category,
  } = good;

  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const declension = useSelector(state => state.city.userCity.declension);

  const stockCode = useSelector(state => state.stock.data.code) || '';
  const lastAddedItem = useSelector(state => state.cart.lastAddedItem);
  const categoriesAliases = useSelector(state => state.catalog.products.categoriesAliases);
  const { isPhone, browserName } = useSelector(state => state.responsive);
  const location = useLocation();
  const timeout = useRef({});
  const [isFlipped, setIsFlipped] = useState(false);

  // лист доступного теста
  const doughs = useMemo(() => {
    return getDoughTypes(variations);
  }, [variations]);

  const [dough, setDough] = useState(variations[0]?.kind?.id);
  const [selectedCrust, setSelectedCrust] = useState(null);
  const [size, setSize] = useState(variations[0]?.size?.value);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const resetCrust = useMemo(
    () => ({
      reset: () => {},
    }),
    [],
  );

  const product = useMemo(() => {
    return qs.parse(location.search, { parseNumbers: true }).product;
  }, [location.search]);

  // лист вариаций с выбранным тестом. если у товара нет теста, то просто лист вариаций
  const variationsByDough = isPizza(variations[0])
    ? variations.filter(variant => variant.kind !== null && variant.kind.id === Number(dough))
    : variations;

  // лист бортов для выбранного теста и размера
  const stuffedCrustsAvailable = getStuffedCrustsAvailable(variationsByDough, size);

  // лист бортов для выбранного теста и размера
  const stuffedCrusts = useMemo(() => {
    return getStuffedCrusts(variationsByDough, size);
  }, [variationsByDough, size]);

  const [stuffedCrust, setStuffedCrust] = useState(() => {
    return stuffedCrusts.includes('none') ? 'none' : stuffedCrusts[0];
  });

  // лист доступного теста
  const doughTypes = getDoughTypes(variations);
  // вариация исходя из выбранного теста, размера и борта
  const variation = useMemo(() => {
    const newVariation = { ...getVariation(variationsByDough, size, stuffedCrust) };
    const isNewSelectedCrustAvailable = newVariation?.available_ingredients?.some(
      ingredient =>
        ingredient.type === CRUST_EXTRA ||
        ingredient.type === CRUST_EXTRA_EN ||
        ingredient.type === CRUST_EXTRA_PL,
    );
    const newSelectedCrust = newVariation?.available_ingredients?.find(
      ingredient => ingredient.id === selectedCrust?.id,
    );
    if (selectedCrust && isNewSelectedCrustAvailable && newSelectedCrust) {
      newSelectedCrust.count = 1;
      newVariation.added_ingredients = [newSelectedCrust];
      newVariation.available_ingredients = newVariation.available_ingredients.map(ingredient =>
        ingredient.type === CRUST_EXTRA || ingredient.type === CRUST_EXTRA_EN
          ? ingredient.id === selectedCrust.id
            ? { ...ingredient, count: 1 }
            : { ...ingredient, count: 0 }
          : ingredient,
      );
    }
    if (!selectedCrust && isNewSelectedCrustAvailable) {
      newVariation.added_ingredients = [];
      newVariation.available_ingredients = newVariation.available_ingredients.map(ingredient =>
        ingredient.type === CRUST_EXTRA ||
        ingredient.type === CRUST_EXTRA_EN ||
        ingredient.type === CRUST_EXTRA_PL
          ? {
              ...ingredient,
              count: 0,
            }
          : ingredient,
      );
    }
    good.variations = good?.variations.map(item =>
      item.id === newVariation.id ? { ...newVariation } : { ...item },
    );
    return newVariation;
  }, [variationsByDough, size, stuffedCrust, selectedCrust]);

  // лист размеров у выбранного теста
  const sizes = getSizes(variationsByDough);
  // доступен ли какой-то борт кроме стандартного
  const stuffedCrustIsAvailable = getStuffedCrustIsAvailable(variationsByDough, size);
  // количество людей на одну пиццу
  const personCount = getPersonCount(variation);
  // количество удаленных ингредиентов
  const removedIngredientsCount = getRemovedIngredientsCount(variation);

  const {
    price,
    old_price,
    image_list,
    image_cart,
    image_list_webp,
    image_cart_webp,
    characteristics = [],
    include_ingredients,
    available_ingredients,
    is_combo,
  } = variation;

  let goodPrice = price;
  let goodOldPrice = old_price;
  const isCrustAvailable = available_ingredients?.some(ingr => ingr.id === selectedCrust?.id);
  if (selectedCrust && isCrustAvailable) {
    goodPrice += +selectedCrust.price;
    goodOldPrice += +selectedCrust.price;
  }
  const options = useMemo(() => {
    // количество людей на одну пиццу
    const personCount = getPersonCount(variation);

    return [
      isHot(types) && <MetaOption className={cx(`ProductCard__option`)} type="hot" />,
      isHalal(types) && <MetaOption className={cx(`ProductCard__option`)} type="halal" />,
      isVegeterian(types) && <MetaOption className={cx(`ProductCard__option`)} type="vegetarian" />,
      personCount && (
        <MetaOption className={cx(`ProductCard__option`)} type="person-count">
          {personCount}
        </MetaOption>
      ),
    ].filter(Boolean);
  }, [types, variation]);

  const meta = useMemo(() => {
    const result = [
      isNew(types) && <span className={cx(`ProductCard__badge`)}>New</span>,
      !isNew(types) && isHit(types) && <span className={cx(`ProductCard__badge`)}>Hit</span>,
      isHot(types) && <MetaOption className={cx(`ProductCard__option`)} type="hot" />,
      isHalal(types) && <MetaOption className={cx(`ProductCard__option`)} type="halal" />,
      isVegeterian(types) && <MetaOption className={cx(`ProductCard__option`)} type="vegetarian" />,
      personCount && (
        <MetaOption className={cx(`ProductCard__option`)} type="person-count">
          {personCount}
        </MetaOption>
      ),
    ].filter(Boolean);

    return result.length > 0 ? <div className={cx('ProductCard__meta')}>{result}</div> : null;
  }, [types, personCount]);

  // можно ли вызвать окно товара
  const isClickable = useMemo(() => {
    return isFromProductList && good_type !== 'combo' && !isHiddenActions;
  }, [isFromProductList, good_type, isHiddenActions]);

  // открыта/закрыта модалка
  const isOpen = useMemo(() => {
    return isClickable && isModalOpen;
  }, [isClickable, isModalOpen]);

  const changeDough = newDough => {
    setDough(newDough);
  };

  const changeSize = newSize => {
    setSize(newSize);
  };

  const selectStuffedCrust = newStuffedCrust => {
    setStuffedCrust(newStuffedCrust);
  };

  const setIsFlippedFalse = () => {
    setIsFlipped(false);
  };

  const handleRemoveIngredient = ingredientId => {
    removeIngredientAction({ id, variation, ingredientId });
  };

  const handleCancelRemovedIngredients = () => {
    cancelRemovedIngredientsAction({ id, variation });
  };

  // на задней стороне карты удаляем уже добавленный товар и добавляем его с сырным бортом
  const addToCartFromBackside = () => {
    // переворачиваем карту
    setIsFlipped(false);

    // если последний добавленный товар существует (мы могли очистить корзину, например)
    if (lastAddedItem) {
      // eslint-disable-next-line no-shadow
      const { item, type, hash, count } = lastAddedItem;

      // и добавляем вместо него новый с сырным бортом
      const changePizza = () => {
        const cheeseVariation = getVariation(variationsByDough, size, 'cheese');

        const addToCartWithSauce = () => {
          dispatch(addToCart(id, [{ type: 'good', item: cheeseVariation }])).then(() => {
            return cheeseVariation.sauces?.length > 0
              ? dispatch(
                  showFreeSaucesPanel(
                    cheeseVariation.id,
                    cheeseVariation.sauces,
                    cheeseVariation.free_sauce_count,
                  ),
                )
              : null;
          });
        };

        return addToCartWithSauce();
      };

      // если больше одного товара, то делаем -1
      if (count > 1) {
        return dispatch(
          updateItem({
            good_id: item.id,
            type,
            hash,
            count: count - 1,
          }),
        ).then(() => changePizza());
      }

      // если товар один, то удаляем
      return dispatch(removeItem(hash)).then(() => changePizza());
    }

    return null;
  };

  const getSizeSelector = useCallback(
    params => {
      // доступен ли какой-то борт кроме стандартного
      const stuffedCrustIsAvailable = getStuffedCrustIsAvailable(variationsByDough, size);

      const changeDough = newDough => {
        setDough(newDough);
      };

      const changeSize = newSize => {
        setSize(newSize);
      };

      const selectStuffedCrust = newStuffedCrust => {
        setStuffedCrust(newStuffedCrust);
      };

      return isPizza(variations[0]) ? (
        <PizzaSelector
          className={params.className}
          sizes={sizes}
          currentSize={size}
          doughTypes={doughs}
          currentDough={dough}
          stuffedCrustIsAvailable={stuffedCrustIsAvailable}
          stuffedCrustsAvailable={stuffedCrusts}
          activeStuffedCrust={stuffedCrust}
          changeSize={changeSize}
          changeDough={changeDough}
          selectStuffedCrust={selectStuffedCrust}
          toggleStuffedCrust={selectStuffedCrust}
          isBottom={params.isBottom}
          specialStylesMode={params.specialStylesMode}
        />
      ) : (
        <SizeSelector
          className={params.className}
          sizes={sizes}
          currentSize={size}
          onChange={changeSize}
          specialStylesMode={params.specialStylesMode}
        />
      );
    },
    [variationsByDough, sizes, size, doughs, dough, stuffedCrusts, stuffedCrust],
  );

  const onRemoveIngredient = useCallback(
    ingredientId => {
      removeIngredientAction({ id, variation, ingredientId });
    },
    [id, variation],
  );

  const backside = useMemo(() => {
    // если это не мобильная версия, не меню выбора акционного товара, не сам акционный товар и у него есть сырный борт
    if (
      browserName === 'chrome' &&
      !isPhone &&
      !stockRole &&
      !variation.old_price &&
      stuffedCrustsAvailable.includes('cheese')
    ) {
      return (
        <div className={cx('ProductCard__backside')}>
          <CheeseIcon className={cx('ProductCard__backside-icon')} />
          <span className={cx('ProductCard__backside-description')}>
            {intl.formatMessage({ id: 'productcard.cheesecrust' })}
          </span>
          <div className={cx('ProductCard__backside-buttons')}>
            <Button
              onClick={addToCartFromBackside}
              disabled={isAdding}
              className={cx('ProductCard__backside-buttons-yes')}
            >
              {intl.formatMessage({ id: 'question.yes' })}
            </Button>
            <Button
              secondary
              disabled={isAdding}
              onClick={setIsFlippedFalse}
              className={cx('ProductCard__backside-buttons-no')}
            >
              {intl.formatMessage({ id: 'question.no' })}
            </Button>
          </div>
        </div>
      );
    }

    return null;
  }, [
    browserName,
    isPhone,
    variation,
    stuffedCrustsAvailable,
    stockRole,
    isAdding,
    // addToCartFromBackside,
    // setIsFlippedFalse,
  ]);

  const handleAddToCart = async () => {
    if (isHiddenActions) {
      return null;
    }

    // если доступен сырный борт и если он еще не выбран
    if (Boolean(backside) && stuffedCrust === 'none') {
      // то переворачиваем карту товара
      setIsFlipped(true);
      timeout.current = setTimeout(() => {
        setIsFlipped(false);
      }, 8000);
    }
    return addToCartAction({ goodId: id, variation, stockCode });
  };

  const goToComboboxPage = () => {
    if (good_type === 'combo') {
      history.push(getRoute('comboboxAlias', alias));
    }
  };

  useEffect(() => {
    setIsModalOpen(product === id);
  }, [product, id]);

  const onPushToConstructor = useCallback(() => {
    dispatch(changeAvailableVariations(id, variations));

    const queryParams = qs.stringify({
      dough,
      size,
      stuffedCrust,
      fromProduct: 1,
      removedIngredientsIds: include_ingredients
        ?.filter(ingredient => ingredient.removed)
        .map(ingredient => ingredient.id),
    });
    history.push({ pathname: getRoute('constructorAlias', id), search: queryParams });
  }, [
    id,
    variations,
    dough,
    size,
    stuffedCrust,
    include_ingredients,
    history,
    changeAvailableVariations,
  ]);

  const onModalOpen = useCallback(() => {
    history.push({ search: qs.stringify({ product: id }) });
  }, [history, id]);

  const onModalClose = useCallback(() => {
    history.push({ search: null });
  }, [history]);

  useEffect(() => {
    if (!isFlipped && timeout.current) {
      clearTimeout(timeout.current);
    }
  }, [isFlipped]);

  useEffect(() => {
    if (isOpen) {
      document.title = intl.formatMessage(
        { id: 'page.menu.helmet.product.modal' },
        {
          category: categoryName,
          name,
          declension,
        },
      );
      document.querySelector('meta[name="description"]').setAttribute('content', data);
    } else {
      document.title = intl.formatMessage({ id: 'page.menu.helmet' }, { declension });
      document
        .querySelector('meta[name="description"]')
        .setAttribute('content', intl.formatMessage({ id: 'og.title' }));
    }
  }, [isOpen]);

  useEffect(() => {
    if (isPhone && isModalOpenPhone) {
      document.title = intl.formatMessage(
        { id: 'page.menu.helmet.product.modal' },
        {
          category: categoryName,
          name,
          declension,
        },
      );
    } else {
      document.title = intl.formatMessage({ id: 'page.menu.helmet' }, { declension });
      document
        .querySelector('meta[name="description"]')
        .setAttribute('content', intl.formatMessage({ id: 'og.title' }));
    }
  }, [good, isPhone, isModalOpenPhone]);

  return (
    <Flipper className={cx('ProductCard', className)} backside={backside} isFlipped={isFlipped}>
      <picture
        className={cx('ProductCard__picture')}
        style={meta ? null : { marginBottom: '44px' }}
        onClick={isClickable && onModalOpen}
      >
        <source srcSet={isPhone ? image_cart_webp : image_list_webp} type="image/webp" />
        {compact ? (
          <img
            className={cx('ProductCard__image')}
            alt={`${categoriesAliases[category]} ${name}`}
            src={isPhone ? image_cart : image_list}
          />
        ) : (
          <LazyLoadImage
            className={cx('ProductCard__image')}
            alt={`${categoriesAliases[category]} ${name}`}
            src={isPhone ? image_cart : image_list}
            threshold={300}
          />
        )}
      </picture>
      {meta}
      <div className={cx(`ProductCard__header`)} style={data ? null : { marginBottom: '24px' }}>
        {/* Название товара */}
        <Heading className={cx(`ProductCard__heading`)} level={3}>
          {name}
        </Heading>
        <Characteristics characteristics={characteristics} />
        {/* Тултип с характеристиками */}
      </div>
      {/* Описание товара */}
      {data && <Subheading className={cx(`ProductCard__subheading`)}>{data}</Subheading>}
      <div className={cx('ProductCard__actions', { ProductCard__actions_compact: compact })}>
        {/* Селектор вида пиццы (только для пицц) */}
        {isPizza(variations[0]) && sizes.length > 0 && (
          <PizzaSelector
            className={cx(`ProductCard__selector`)}
            sizes={sizes}
            currentSize={size}
            changeSize={changeSize}
            doughTypes={doughTypes}
            currentDough={dough}
            changeDough={changeDough}
            stuffedCrustIsAvailable={stuffedCrustIsAvailable}
            stuffedCrustsAvailable={stuffedCrustsAvailable}
            selectStuffedCrust={selectStuffedCrust}
            toggleStuffedCrust={selectStuffedCrust}
            activeStuffedCrust={stuffedCrust}
          />
        )}
        {/* Селектор размера для непицц */}
        {!isPizza(variations[0]) && sizes.length > 0 && (
          <SizeSelector
            className={cx(`ProductCard__selector`)}
            sizes={sizes}
            currentSize={size}
            onChange={changeSize}
          />
        )}
        {isPizza(good?.variations[0]) ? (
          <Crust
            variation={variation}
            remove={onRemoveIngredient}
            good={good}
            resetCrust={resetCrust}
            selectedCrust={selectedCrust}
            setSelectedCrust={setSelectedCrust}
            specialStylesMode
          />
        ) : null}
        {/* В корзину */}
        {good_type === 'combo' ? (
          <Button
            className={cx('ProductCard__button', { ProductCard__button_compact: compact })}
            onClick={goToComboboxPage}
            disabled={isHiddenActions}
          >
            {intl.formatMessage({ id: 'combobox.card.button' })}
          </Button>
        ) : (
          <Button
            className={cx('ProductCard__button', { ProductCard__button_compact: compact })}
            onClick={handleAddToCart}
            isLoading={isAdding}
            disabled={isHiddenActions || (isLoadingStockApply && !isAdding)}
          >
            {intl.formatMessage({ id: `${type === 'gift' ? 'button.get' : 'button.inCart'}` })}
          </Button>
        )}
        {/* Цена */}
        {type === 'gift' ? (
          <div
            className={cx('ProductCard__price', 'ProductCard__gift', {
              ProductCard__price_compact: compact,
            })}
          >
            <GiftIcon width={32} height={32} />
          </div>
        ) : (
          <Amount
            className={cx('ProductCard__price', { ProductCard__price_compact: compact })}
            value={goodOldPrice || goodPrice}
            newValue={goodPrice}
            variation={variation}
          />
        )}
      </div>

      {isAlcohol(types) && <AlcoholWarning />}
      {Boolean(show_ingredients_button) &&
        (!_isEmpty(include_ingredients) || !_isEmpty(available_ingredients)) && (
          <Ingredients
            stockRole={stockRole}
            id={id}
            variationId={variation.id}
            include_ingredients={include_ingredients}
            isAdding={isAdding}
            removedIngredientsCount={removedIngredientsCount}
            variations={variations}
            dough={dough}
            size={size}
            stuffedCrust={stuffedCrust}
            addToCart={handleAddToCart}
            onRemove={handleRemoveIngredient}
            onCancel={handleCancelRemovedIngredients}
            onChangeAvailableVariations={changeAvailableVariations}
            isPromo={isPromo}
          />
        )}
      {isOpen && (
        <ProductCardModal
          isOpen={isOpen}
          good={good}
          variation={variation}
          variations={good?.variations}
          options={options}
          getSizeSelector={getSizeSelector}
          pushToConstructor={onPushToConstructor}
          modalClose={onModalClose}
          removeIngredient={handleRemoveIngredient}
          addToCart={handleAddToCart}
          remove={onRemoveIngredient}
          resetCrust={resetCrust}
          selectedCrust={selectedCrust}
          setSelectedCrust={setSelectedCrust}
          value={goodOldPrice || goodPrice}
          newValue={goodPrice}
        />
      )}
    </Flipper>
  );
}
